import React, { Dispatch, SetStateAction, useMemo } from "react";
import { useRouteMatch } from "react-router-dom";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Button, Stack } from "@mui/material";
import { ToggleButtons } from "components/reports/Toggle-Buttons/ToggleButtons";
import { MaterialTooltip } from "components/tooltips/MaterialTooltip";
import { useModals, useNavigate } from "hooks";
import { useAccountSelector } from "store";

import { Routes } from "../../../constants";
import { UploadFilesMode } from "./FilesUpload";

interface Props {
  isDisabled: boolean;
  step: number;
  onSubmit: () => void;
  onBack?: () => void;
  dealId?: number | null;
  folderId?: number | null;
  hasPdfFiles?: boolean;
  hasDeal?: boolean;
  hasFiles?: boolean;
  hasAllDocumentTypes?: boolean;
  openDataExtractor?: boolean;
  isScanning?: boolean;
  isCloudMirror?: boolean;
  setUploadFilesMode: Dispatch<SetStateAction<UploadFilesMode>>;
  uploadFilesMode: UploadFilesMode;
  nextLabel?: string;
}

export const Footer: React.FC<Props> = ({
  isDisabled,
  step,
  onSubmit,
  onBack,
  dealId,
  folderId,
  hasPdfFiles,
  hasDeal,
  hasFiles,
  hasAllDocumentTypes,
  openDataExtractor,
  isScanning,
  isCloudMirror,
  setUploadFilesMode,
  uploadFilesMode,
  nextLabel
}) => {
  const { closeFilesUploadModal } = useModals();
  const navigate = useNavigate();
  const { show_recommendations } = useAccountSelector() || {};

  const handleGoToWorkspace = () => {
    closeFilesUploadModal();
    if (isGlobalWorkspace) {
      if (folderId) {
        navigate({ route: "workspaceFolder", params: { folder_id: folderId } });
      } else {
        navigate({ route: "workspaceFolders" });
      }
    } else if (dealId && folderId) {
      navigate({
        route: "dealFolder",
        params: { deal_id: dealId, folder_id: folderId },
        search: { section: folderId }
      });
    } else if (dealId) {
      navigate({ route: "dealWorkspace", params: { deal_id: dealId } });
    }
  };

  const isGlobalWorkspace = useMemo(() => dealId === -1, [dealId]);

  const isDealWorkspaceRoute = useMemo(() => {
    return (
      !!useRouteMatch({ path: Routes.dealWorkspace, exact: true }) ||
      !!useRouteMatch({ path: Routes.dealFolder, exact: true })
    );
  }, []);

  return (
    <Stack
      sx={{
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "8px",
        p: "24px"
      }}
    >
      <Stack direction="row" sx={{ flex: 1 }} gap="8px">
        {step === 2 && onBack && (
          <Button
            disableRipple
            startIcon={<ArrowBackIcon sx={{ fontSize: 24 }} />}
            onClick={onBack}
            sx={{
              color: "#26a6ae",
              fontSize: 15,
              fontWeight: 400,
              textTransform: "none",
              minWidth: 0,
              px: 0,
              "&:hover": { bgcolor: "transparent", color: "#1e8590" }
            }}
          >
            Back
          </Button>
        )}
        {step === 3 && !openDataExtractor && (
          <Button
            variant="outlined"
            sx={{
              borderColor: "#7b92a9",
              color: "#071f35",
              fontSize: "16px",
              textTransform: "none",
              borderRadius: "32px"
            }}
            onClick={closeFilesUploadModal}
          >
            Close
          </Button>
        )}
        {step !== 3 && show_recommendations && (
          //available before upload files
          <Stack direction="row" gap="8px" alignItems="center">
            <ToggleButtons
              sx={{
                borderColor: "#7b92a9",
                "& .Mui-selected, & .Mui-selected:hover": {
                  backgroundColor: "#0B4549 !important",
                  color: "white !important"
                }
              }}
              value={uploadFilesMode}
              exclusive
              clearable={false}
              onChange={(_, value) => value && setUploadFilesMode(value as UploadFilesMode)}
              buttons={[
                { value: UploadFilesMode.Review, text: "Review" },
                { value: UploadFilesMode.Commit, text: "Commit" }
              ]}
            />
            <MaterialTooltip
              isShow
              title={
                uploadFilesMode === UploadFilesMode.Review
                  ? "After upload, review extracted data and approve measures before saving"
                  : "Extracted data is saved to Cashflow and Data automatically when processing completes"
              }
            >
              <InfoOutlinedIcon sx={{ fontSize: 20, color: "#92A7BC" }} />
            </MaterialTooltip>
          </Stack>
        )}
      </Stack>
      <Stack direction="row" gap="8px" alignItems="center">
        {step < 3 && (
          <Button
            variant="outlined"
            sx={{
              borderColor: "#7b92a9",
              color: "#071f35",
              fontSize: "16px",
              textTransform: "none",
              borderRadius: "32px"
            }}
            onClick={closeFilesUploadModal}
          >
            Cancel
          </Button>
        )}
        {step === 3 &&
          !openDataExtractor &&
          !isCloudMirror &&
          Boolean(dealId) &&
          !isGlobalWorkspace &&
          !isDealWorkspaceRoute && (
            <MaterialTooltip isShow={!!isScanning} title="Files are being scanned for security. Please wait.">
              <span>
                <Button
                  variant="outlined"
                  disabled={isDisabled}
                  sx={{
                    borderColor: "#7b92a9",
                    color: "#071f35",
                    fontSize: "16px",
                    textTransform: "none",
                    borderRadius: "32px"
                  }}
                  onClick={handleGoToWorkspace}
                >
                  Go to Deal Workspace
                </Button>
              </span>
            </MaterialTooltip>
          )}
        {step === 3 && !openDataExtractor && !isCloudMirror && isGlobalWorkspace && (
          <MaterialTooltip isShow={!!isScanning} title="Files are being scanned for security. Please wait.">
            <span>
              <Button
                variant="outlined"
                disabled={isDisabled}
                sx={{
                  borderColor: "#7b92a9",
                  color: "#071f35",
                  fontSize: "16px",
                  textTransform: "none",
                  borderRadius: "32px"
                }}
                onClick={handleGoToWorkspace}
              >
                Go to Workspace
              </Button>
            </span>
          </MaterialTooltip>
        )}
        {step === 3 && !openDataExtractor && !isCloudMirror && hasPdfFiles && (
          <MaterialTooltip isShow={!!isScanning} title="Files are being scanned for security. Please wait.">
            <span>
              <Button
                onClick={onSubmit}
                variant="contained"
                disabled={isDisabled}
                sx={{
                  bgcolor: "#071f35",
                  color: "white",
                  fontSize: "16px",
                  textTransform: "none",
                  borderRadius: "32px",
                  "&:hover": { bgcolor: "#0a3142" },
                  "&:disabled": { bgcolor: "#bac7ce", color: "white" }
                }}
              >
                Continue to Data Extractor
              </Button>
            </span>
          </MaterialTooltip>
        )}
        {step < 3 && (
          <>
            <Button
              onClick={onSubmit}
              variant="contained"
              disabled={isDisabled}
              sx={{
                bgcolor: "#071f35",
                color: "white",
                fontSize: "16px",
                textTransform: "none",
                borderRadius: "32px",
                "&:hover": { bgcolor: "#0a3142" }
              }}
            >
              {step === 1 && nextLabel ? nextLabel : "Next"}
            </Button>
            {isDisabled &&
              step === 1 &&
              (() => {
                const hints: string[] = [];
                if (!isCloudMirror && !hasDeal) hints.push("a deal");
                if (!hasFiles) hints.push("files");
                if (!hints.length) return null;
                const message = `Please select ${hints.join(" and ")} to continue`;
                return (
                  <MaterialTooltip isShow title={message}>
                    <InfoOutlinedIcon sx={{ fontSize: 20, color: "#92A7BC" }} />
                  </MaterialTooltip>
                );
              })()}
            {isDisabled && step === 2 && isCloudMirror && !hasAllDocumentTypes && (
              <MaterialTooltip isShow title="Please select classification to continue">
                <InfoOutlinedIcon sx={{ fontSize: 20, color: "#92A7BC" }} />
              </MaterialTooltip>
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};
